'use client';

import {
  isEmptyString,
} from '@/utils/misc';
import {
  useCallback,
  useEffect,
} from 'react';
import isBoolean from 'lodash/isBoolean';
import keys from 'lodash/keys';
import {
  googleMapsApiKey,
} from '@/env';
import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  sendRequest,
} from '@/utils/httpClient';
import isNil from 'lodash/isNil';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import {
  PatientOrgMappingDTO,
} from '@/dtos';
import {
  getFullName,
} from '@/utils/string';

export function isEmptyPatientValue(prop: any, key: string) {
  return isEmptyString(prop)
    || (/phone|fax/i.test(key) && prop === '+1')
    || (isBoolean(prop) && !prop);
}

export default function useConditionalRequirementCallback(
  values: Record<string, string>,
) {
  return useCallback((value: string) => {
    const fields: Record<string, string> = values;
    const isRequired = keys(fields).some(
      (fieldKey: string) => !isEmptyPatientValue(fields[fieldKey], fieldKey),
    );
    return isRequired ? !isEmptyString(value) : true;
  }, [values]);
}

export function useGoogleMaps(isAuthenticated: boolean) {
  useEffect(() => {
    if (isEmptyString(googleMapsApiKey)) {
      console.error('Missing Google Maps API key.');

      return undefined;
    }

    if (!isAuthenticated) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [googleMapsApiKey, isAuthenticated]);
}

export function getLookupOptions(
  optionLookupUrl: string,
  search: string,
  page?: number,
  pageSize?: number,
) {
  return sendRequest<any[]>({
    url: optionLookupUrl,
  }, {
    method: 'GET',
    params: { search, page, pageSize },
  });
}

export function useSearchLookupOptions(
  optionLookupUrl: string,
  searchQuery: string,
  enabled: boolean,
  page?: number,
  pageLimit?: number,
): UseQueryResult<any[], Error> {
  return useQuery<any[], Error>({
    queryKey: ['filter-lookup', optionLookupUrl, searchQuery, page, pageLimit],
    queryFn: () => getLookupOptions(optionLookupUrl, searchQuery, page, pageLimit),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useDatadog() {
  const setDatadogUser = (userInfo: PatientOrgMappingDTO & {patientId?: number} | null) => {
    if (!isNil(userInfo)) {
      const { patientId = 0 } = userInfo;
      datadogLogs.setUser({
        id: patientId?.toString(),
        name: getFullName(userInfo) ?? '',
      });
    }
  };
  return { setDatadogUser };
}